
<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import {
  HomeIcon,
  TruckIcon,
  ArchiveBoxIcon,
  DocumentChartBarIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  Cog6ToothIcon,
} from "@heroicons/vue/24/outline";
import { useToaster } from "ram-components";
import { useUserDataStore } from "@/stores/userDataStore";

const toaster = useToaster({
  position: "bottom-right",
  closeOnClick: false,
  pauseOnHover: true,
  showCloseOnHover: true,
  timeout: 6000,
});

interface Props {
  isMobileOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isMobileOpen: false,
});

const emit = defineEmits<{
  (e: "close"): void;
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const userDataStore = useUserDataStore();

const navItems = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: HomeIcon,
    roles: ["Processing Clerk", "Full Access"],
  },
  {
    name: "Receiving",
    path: "/receiving",
    icon: TruckIcon,
    roles: ["Processing Clerk", "Full Access"],
  },
  {
    name: "Collections",
    path: "/collections",
    icon: ArchiveBoxIcon,
    roles: ["Processing Clerk", "Full Access"],
  },
  {
    name: "Reports",
    path: "/reports",
    icon: DocumentChartBarIcon,
    roles: ["Processing Clerk", "Full Access"],
  },
  {
    name: "Settings",
    path: "/settings",
    icon: Cog6ToothIcon,
    roles: ["Full Access"],
  },
];

const isActive = (path: string) => route.path === path;

const handleLogout = async () => {
  try{

    await authStore.logout();
 
  router.push("/login");
   toaster.push({
    type: "success",
    text: "Logged Out Successfully!",
  });
  }catch(error){
    
  }
};

const handleNavClick = (path: string) => {
  router.push(path);
  emit("close");
};

const filteredNavItems = computed(() => {
 const userEmail = authStore.user?.emailAddress?.toLowerCase();
  const userRole = authStore.userRole;

  if (!userRole) return [];

  if (userEmail === "superuser@ram.co.za") {
    return navItems.filter((item) => item.name === "Settings");
  }

  return navItems.filter((item) => {
    if (item.name === "Settings") {
      return userRole === "Full Access";
    }

    return item.roles.includes(userRole);
  });
});

const displayName = computed(() => {
  if (authStore.user) {
    return `${authStore.user.firstName} ${authStore.user.lastName}`;
  }
  return "Guest";
});

const displayInitials = computed(() => {
  if (authStore.user?.firstName && authStore.user?.lastName) {
    return `${authStore.user.firstName[0]}${authStore.user.lastName[0]}`;
  }
  return "U";
});
</script>
<template>
  <aside
    :class="[
      'flex h-full w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-300',
      'lg:translate-x-0',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
    ]"
  >
    <div class="border-b border-gray-200 p-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center">
            <img src="/src/assets/img/sb_logo.png" alt="sbsa_logo" />
          </div>
          <div>
            <h1 class="font-bold text-gray-800">SBSA Pick Up Point</h1>
          </div>
        </div>
        <button
          @click="emit('close')"
          class="rounded p-1 hover:bg-gray-100 lg:hidden"
        >
          <XMarkIcon class="h-5 w-5 text-gray-500" />
        </button>
      </div>
    </div>

    <nav class="flex-1 p-4">
      <ul class="space-y-1">
        <li v-for="item in filteredNavItems" :key="item.path">
          <button
            @click="handleNavClick(item.path)"
            :class="[
              'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors',
              isActive(item.path)
                ? 'bg-ram-red text-white'
                : 'text-gray-600 hover:bg-gray-100',
            ]"
          >
            <component :is="item.icon" class="h-5 w-5" />
            <span class="font-medium">{{ item.name }}</span>
          </button>
        </li>
      </ul>
    </nav>

    <div class="border-t border-gray-200 p-2">
      <div class="mb-2 flex items-center gap-3 px-4 py-3">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200"
        >
          <span class="text-sm font-medium text-gray-600">
            {{ displayInitials }}
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-900">
            {{ displayName }}
          </p>
          <p class="whitespace-normal break-all text-xs text-gray-500">
            {{ authStore.user?.emailAddress || "No Email" }}
          </p>
        </div>
      </div>
      <button
        @click="handleLogout"
        class="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-gray-600 transition-colors hover:bg-gray-100"
      >
        <ArrowRightOnRectangleIcon class="h-5 w-5" />
        <span class="font-medium">Logout</span>
      </button>
    </div>
  </aside>
</template>
